import type {
  CtaHeaderProps,
  FooterV4Props,
  FootnotesProps,
  HeaderV4Props,
} from '@jouzen/ecom-components/layout';
import {
  defaultHeaderData,
  defaultHeaderSecondaryLinks,
  useHeaderContext,
} from '@jouzen/ecom-components/layout';
import { useDynamicConfig, useGateValue } from '@statsig/react-bindings';
import { noop } from 'lodash';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';

import type { EyebrowConfig } from '@/app/components/Eyebrow/types';
import useDiscountMessage from '@/app/components/PageLayout/_hooks/useDiscountMessage';
import { useCartContext } from '@/app/contexts/CartProvider';
import useFootnotesProps from '@/app/hooks/useFootnotesProps';
import useLanguageSwitcher from '@/app/hooks/useLanguageSwitcher';
import usePathnameWithoutLocale from '@/app/hooks/usePathnameWithoutLocale';

import useEyebrowConfig from './useEyebrowConfig';
import useFooterConfig from './useFooterConfig';
import usePageAnalytics from './usePageAnalytics';

interface PageLayoutProps {
  enableScrollAnimation: boolean;
  isCarouselInView: boolean;
  parentCtaHeaderProps?: CtaHeaderProps | undefined;
  parentFooterProps?: Partial<FooterV4Props> | undefined;
  parentHeaderProps?: Partial<HeaderV4Props> | undefined;
}

const usePageLayout = ({
  enableScrollAnimation = false,
  isCarouselInView = false,
  parentCtaHeaderProps,
  parentFooterProps,
  parentHeaderProps,
}: PageLayoutProps) => {
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const { inverse } = useHeaderContext()!;
  const currentPath = usePathname();
  const router = useRouter();
  const { refreshCart } = useCartContext();
  const cart = refreshCart?.cart;
  const { locale, onLocaleChange } = useLanguageSwitcher();
  const footnotePathname = usePathnameWithoutLocale();
  const { displayMessage } = useDiscountMessage({
    isAccessory: false,
  });
  const config = useDynamicConfig('eyebrow');
  const eyebrowConfig = config.value as EyebrowConfig;
  const activeEyebrowConfig = Object.keys(config.value).length > 0;

  const eyebrowMessageType = useEyebrowConfig({
    hasActiveStatsigConfig: activeEyebrowConfig,
    hasDiscountMessage: Boolean(displayMessage),
    statsigValue: eyebrowConfig,
  });
  const shouldUseUpdatedMembershipFootnote = useGateValue('chf_rollout');
  const footnotesProps: FootnotesProps = useFootnotesProps(
    footnotePathname,
    shouldUseUpdatedMembershipFootnote,
  );
  const {
    onFooterLinkClick,
    onHamburgerClick,
    onMenuLinkClick,
    sendEmailSubmitAnalytics,
  } = usePageAnalytics();
  const footerLinks = useFooterConfig();
  const legalLinks = footerLinks?.legalLinks;
  const _legalLinks = legalLinks ? { legalLinks } : undefined;

  const isEU = useGateValue('is_european_union');
  const isSteloEnabled = useGateValue('dexcom_rollout');
  const isUSAOnly = useGateValue('usa_only');

  const handleShoppingCartClick = useCallback((): void => {
    router.push(`/${locale}/cart`);
  }, [locale, router]);

  const handleNewsletterFormSubmit = (email: string): void => {
    void sendEmailSubmitAnalytics(email).catch(noop);
    if (email) setEmailSubmitted(true);
  };

  const headerPosition: HeaderV4Props['headerPosition'] =
    parentHeaderProps?.headerPosition === 'static' ? 'static' : 'fold';

  const headerProps: HeaderV4Props = {
    count: cart?.itemCountForIcon ?? 0,
    currentPath,
    enableScrollAnimation,
    headerConfig: defaultHeaderData({
      isGiftCardEnabled: isUSAOnly,
      isSteloEnabled,
    }),
    headerPosition,
    inverse,
    isCarouselInView,
    onHamburgerClick,
    onMenuLinkClick,
    onShoppingCartClick: handleShoppingCartClick,
    secondaryLinks: defaultHeaderSecondaryLinks,
    ...parentHeaderProps,
  };

  const footerProps = {
    currentPath,
    emailSubmitted,
    isLegalLinksLocalized: Boolean(_legalLinks),
    ..._legalLinks,
    onLinkClick: onFooterLinkClick,
    onLocaleChange: onLocaleChange,
    onNewsletterFormSubmit: handleNewsletterFormSubmit,
    selectedLocale: locale,
    showImpressum: isEU,
    ...parentFooterProps,
  };

  // only pass ctaHeaderProps to layout if it is defined
  const ctaHeaderProps = parentCtaHeaderProps
    ? {
        ctaHeaderProps: {
          inverse: headerProps.inverse ?? false,
          ...parentCtaHeaderProps,
        },
      }
    : {};

  return {
    ctaHeaderProps,
    displayMessage,
    eyebrowConfig,
    eyebrowMessageType,
    footerProps,
    footnotesProps,
    headerProps,
  };
};

export default usePageLayout;
